import React from 'react';

import { ColorTokenProp, ColorToken } from '../../../theme/tokens/color';
import { ButtonColorToken } from '../../../theme/tokens/button-color';
import { mapTokenProp } from '../../../theme/tokens/utils';
import { getTextColorClass } from '../../../utils/colorTokenToTailwind';

import { DEFAULT_BUTTON_TEXT } from '../../CtaWidget/shared';
import {
  ctaWidgetEnum,
  DEFAULT_LABEL,
  DEFAULT_PLACEHOLDER
} from '../../CtaWidget/shared';
import { NodeProps } from './shared';

import { LinkButton } from '../../Link';
import { ImageWith7030TextEmbed } from '../../ImageWith7030Text/ImageWith7030TextEmbed';
import CtaWidget from '../../CtaWidget';
import { PageBlocks } from '../../Page/PageBlocks';

import { BannerCTA } from '~/components/LearningCenter/Components/BannerCTA';
import { ILearningCenterBannerCtaFields } from '@/types/contentful';
import cx from 'classnames';

interface EmbeddedEntryNodeProps extends NodeProps {
  colour: ColorTokenProp | string;
  func?: () => void;
  width?: number | 'auto';
}

export function EmbeddedEntryNode({
  node,
  colour,
  width,
  func
}: EmbeddedEntryNodeProps) {
  const { fields, sys } = node.data?.target ?? {};

  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (func) func();

    window.location.href = fields?.url;
  };

  switch (sys?.contentType?.sys?.id) {
    case 'button':
      const { file, description } = fields?.icon?.fields ?? {};
      const iconSrc = file?.url ? `https:${file?.url}` : undefined;
      const iconAlt = description ?? undefined;

      return (
        <>
          <LinkButton
            href={fields?.url}
            className={cx({
              '!hidden desktop:!inline-flex': fields?.mobileUrl
            })}
            {...(func && { onClick: handleClick })}
            target={fields?.openInNewTab ? '_blank' : ''}
            rel="noopener noreferrer"
            buttonColor={mapTokenProp<ColorToken, ButtonColorToken>(
              colour,
              (c) => (c === 'white' ? 'light' : 'dark')
            )}
            buttonHoverColor={mapTokenProp<ColorToken, ButtonColorToken>(
              colour,
              (c) => (c === 'white' ? 'lightHover' : 'darkHover')
            )}
            attachParams={fields?.forwardedButtonParams !== undefined}
            forwardedButtonParams={fields?.forwardedButtonParams}
          >
            <div className="flex items-center justify-around">
              {fields?.text}
              {iconSrc && (
                <div className="relative ml-3 mr-2">
                  <img src={iconSrc} alt={iconAlt} loading="lazy" />
                </div>
              )}
            </div>
          </LinkButton>
          {fields?.mobileUrl && (
            <LinkButton
              href={fields?.mobileUrl}
              className="!inline-flex desktop:!hidden"
              target={fields?.openInNewTab ? '_blank' : ''}
              rel="noopener noreferrer"
              {...(func && { onClick: handleClick })}
              buttonColor={mapTokenProp<ColorToken, ButtonColorToken>(
                colour,
                (c) => (c === 'white' ? 'light' : 'dark')
              )}
              buttonHoverColor={mapTokenProp<ColorToken, ButtonColorToken>(
                colour,
                (c) => (c === 'white' ? 'lightHover' : 'darkHover')
              )}
              attachParams={fields?.forwardedButtonParams !== undefined}
              forwardedButtonParams={fields?.forwardedButtonParams}
            >
              <div className="flex items-center justify-around">
                {fields?.text}
                {iconSrc && (
                  <div className="relative ml-3 mr-2">
                    <img src={iconSrc} alt={iconAlt} loading="lazy" />
                  </div>
                )}
              </div>
            </LinkButton>
          )}
        </>
      );

    case 'disclaimer':
      return (
        <p
          className={`text-[0.8125rem] leading-[1.25rem] desktop:text-sm desktop:leading-[1.25rem] ${getTextColorClass(
            colour
          )}`}
        >
          {fields?.text}
        </p>
      );

    case 'imageWith7030Text':
      return <ImageWith7030TextEmbed {...node?.data?.target?.fields} />;

    case 'learningCenterBannerCta':
      return <BannerCTA {...(fields as ILearningCenterBannerCtaFields)} />;

    case 'phoneNumberField':
      return (
        <div className="h-9 min-w-[220px] max-w-[270px] tablet:h-10 tablet:min-w-[254px] tablet:max-w-[270px] desktop:h-10 desktop:min-w-[260px] desktop:max-w-[390px]">
          <CtaWidget
            id={`phone-number-widget-${sys.id}`}
            module="Rich Text"
            errorPosition="bottom"
            width="100%"
            sysId="legacyCta"
            designType={ctaWidgetEnum.MOBILE_PHONE_NUMBER}
            placeholder={DEFAULT_PLACEHOLDER}
            label={DEFAULT_LABEL}
            cfButtonColor={fields?.buttonBackgroundColor || 'green'}
            cfButtonHoverColor={
              fields?.buttonHoverBackgroundColor || 'sweetmint'
            }
            buttonContent={DEFAULT_BUTTON_TEXT}
          />
        </div>
      );

    case 'registrationEntrypoint':
      return (
        <CtaWidget
          {...fields}
          id="rich-text-cta"
          module="Rich Text"
          sysId={sys?.contentful?.sys?.id}
          height={56}
          width={width || 180}
        />
      );

    default:
      return (
        <div className="-mx-[8.333%] my-6 tablet:-mx-[12.5%] tablet:my-8">
          <PageBlocks {...({ pageBlocks: [node?.data?.target] } as any)} />
        </div>
      );
  }
}
